import { useMemo } from 'react';
import { css, tw } from 'twind/css';
import { Maybe } from 'graphql/jsutils/Maybe';

import { ProductStory as ProductStoryTypes } from '__generated__/graphql';
import { parseHtml } from 'utils/html';
import { SafeHtml } from 'ui/elements/SafeHtml';
import { useTranslate } from 'hooks/useTranslations';
import { useFeature } from 'hooks/useFeature';
import { isServer } from 'utils/constants';

import ProductStoryKeywords from './ProductStoryKeywords';
import { SanityContentSlot } from './SanityContentSlot';

const productStoryStylesCommon = css`
  & {
    @apply bg-puma-black-700 bg-opacity-70 py-8 px-6 md:px-10 text-puma-black max-w-full;
  }

  & h3 {
    @apply text-xl font-bold pt-12 pb-4;
  }

  & h3:first-child {
    @apply pt-0;
  }
`;

const productStoryStylesWithBullets = css`
  ${productStoryStylesCommon}

  & ul {
    text-indent: -1rem;
    margin-left: 1rem;
    @apply list-disc list-inside;
  }

  & li {
    @apply py-1;
  }

  & li:last-child {
    @apply pb-0;
  }

  & li::marker {
    @apply text-puma-gold;
  }
`;

const productStoryStylesWithoutBullets = css`
  ${productStoryStylesCommon}

  & ul {
    text-indent: 0;
    margin-left: 0;
  }

  & li {
    @apply py-4;
    @apply border-b-1;
    @apply border-puma-grey-light;
  }
`;

export const ProductStory = ({
  longDescription,
  materialComposition,
  careInstructions,
  manufacturerInfo,
  productKeywords,
  additionalContentSections,
  documentId,
  slotId,
}: ProductStoryTypes & {
  documentId: Maybe<string>;
  slotId: string;
}) => {
  const t = useTranslate();
  const showProductKeywordsEnabled = useFeature('SHOW_PRODUCT_KEYWORDS');
  const removeBulletsFromListsOnPdp = useFeature('HIDE_BULLETS_LISTS_PDP');

  const productStoryStyles = removeBulletsFromListsOnPdp
    ? productStoryStylesWithoutBullets
    : productStoryStylesWithBullets;

  const { manufacturerAddress, countryOfOrigin } = manufacturerInfo || {};

  const { body, lists } = useMemo(() => {
    const body: string[] = [];
    const lists: string[] = [];

    if (!isServer && longDescription) {
      const html = parseHtml(longDescription);
      const nodes = [...(html?.childNodes ?? [])];
      while (nodes.length > 0) {
        const node = nodes[0] as HTMLElement;
        const next = nodes[1] as HTMLElement;
        if (node?.tagName === 'H3' && next?.tagName === 'UL') {
          lists.push(`${node.outerHTML}${next.outerHTML}`);
          nodes.splice(0, 2);
        } else {
          body.push(node.outerHTML || node.textContent || '');
          nodes.splice(0, 1);
        }
      }
    }

    return { body, lists };
  }, [longDescription]);

  return (
    <section
      id="product-story"
      aria-label="Product Story"
      className={tw(productStoryStyles)}
    >
      {isServer ? (
        longDescription
      ) : (
        <section className="grid gap-8 lg:gap-10 grid-cols-1 md:grid-cols-2">
          {body.length > 0 && (
            <div className="col-span-full">
              {body.map(item => (
                <SafeHtml key={item} html={item} />
              ))}
            </div>
          )}
          {lists.map(item => (
            <SafeHtml key={item} html={item} />
          ))}
          {materialComposition?.length ? (
            <div>
              <h3 className="capitalize">{t('materialInformation')}</h3>
              <ul>
                {materialComposition?.map(material => (
                  <li key={material}>{material}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {careInstructions?.length ? (
            <div>
              <h3 className="capitalize">{t('careInstructions')}</h3>
              <ul>
                {careInstructions?.map(instruction => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {manufacturerAddress?.content?.length ? (
            <div data-test-id="manufacturer-address-container">
              {manufacturerAddress?.label ? (
                <h3 className="capitalize">{manufacturerAddress?.label}</h3>
              ) : null}
              <address
                data-test-id="manufacturer-address-details"
                className="not-italic flex flex-col"
              >
                {manufacturerAddress?.content?.filter(Boolean).map(content => (
                  <span className="py-1" key={content}>
                    {content}
                  </span>
                ))}
              </address>
            </div>
          ) : null}
          {countryOfOrigin?.content?.length ? (
            <div data-test-id="country-of-origin-container">
              {countryOfOrigin?.label ? (
                <h3 className="capitalize">{countryOfOrigin?.label}</h3>
              ) : null}
              {countryOfOrigin?.content?.map(content => (
                <div data-test-id="country-of-origin-details" key={content}>
                  {content}
                </div>
              ))}
            </div>
          ) : null}

          <SanityContentSlot
            items={additionalContentSections}
            slotId={slotId}
            documentId={documentId}
          />
        </section>
      )}
      {showProductKeywordsEnabled && (
        <ProductStoryKeywords productKeywords={productKeywords} />
      )}
    </section>
  );
};
